import React from "react"
import { Collapsible } from "../collapsible"
import { Icon } from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { cn } from "../utils"

// =============================================================================
// Props
// =============================================================================

export type StepperItemState = "upcoming" | "active" | "completed"

export type StepperItemProps = {
  /** The step's position in the flow relative to the current step */
  state: StepperItemState
  /** Whether this step can expand to reveal more content. Defaults to false. */
  expandable?: boolean
  /** Whether the step starts expanded (uncontrolled). Only applies when expandable. */
  defaultOpen?: boolean
  /** Whether the step is expanded (controlled). Only applies when expandable. */
  open?: boolean
  /** Called when the step is expanded or collapsed. Only applies when expandable. */
  onOpenChange?: (open: boolean) => void
  /**
   * The step's label when not expandable. When expandable, compose with
   * Stepper.ItemTrigger and Stepper.ItemContent instead.
   */
  children?: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

const rowStyles =
  "relative flex gap-3 pb-6 [counter-increment:stepper-item] last:pb-0 last:[&>[data-slot=stepper-item-connector]]:hidden"

const indicatorStyles =
  "relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"

const indicatorFilledStyles = "bg-gray-900 text-white"
const indicatorUpcomingStyles = "border border-gray-200 bg-white text-gray-900"

// spans from the bottom of this step's indicator (size-6, so top-6) down to
// the top of the next step's indicator (this item's own bottom-0, which the
// pb-6 gap puts right at the next item's box)
const connectorStyles = "absolute left-3 top-6 bottom-0 w-px"

const labelStyles =
  "flex min-h-6 items-center text-sm font-semibold text-gray-900"

// =============================================================================
// Component
// =============================================================================

/**
 * A single step in the flow: a numbered or checked indicator, a connector to
 * the next step, and either a plain label or (when `expandable`) a trigger
 * and content composed with `Stepper.ItemTrigger` and `Stepper.ItemContent`.
 *
 * Must be a descendant of `Stepper`.
 */
export function StepperItem({
  state,
  expandable = false,
  defaultOpen,
  open,
  onOpenChange,
  children
}: StepperItemProps) {
  const completed = state === "completed"

  const indicator = (
    <span
      data-slot="stepper-item-indicator"
      className={cn(
        indicatorStyles,
        completed || state === "active"
          ? indicatorFilledStyles
          : indicatorUpcomingStyles
      )}
    >
      {completed ? (
        <>
          <Icon name="check-mini" size="xs" />
          <VisuallyHidden> (Completed)</VisuallyHidden>
        </>
      ) : (
        <span
          aria-hidden="true"
          className="before:content-[counter(stepper-item)]"
        />
      )}
    </span>
  )

  const connector = (
    <span
      data-slot="stepper-item-connector"
      className={cn(connectorStyles, completed ? "bg-gray-900" : "bg-gray-200")}
    />
  )

  const content = expandable ? (
    <Collapsible
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className="min-w-0 flex-1"
    >
      {children}
    </Collapsible>
  ) : (
    <span
      data-slot="stepper-item-label"
      className={cn(labelStyles, "min-w-0 flex-1")}
    >
      {children}
    </span>
  )

  return (
    <li
      data-slot="stepper-item"
      data-state={state}
      aria-current={state === "active" ? "step" : undefined}
      className={cn(rowStyles)}
    >
      {connector}
      {indicator}
      {content}
    </li>
  )
}

StepperItem.displayName = "Stepper.Item"
